// ✅ src/pages/VehicleV2/LubeRequirementReport.jsx
// Production listing template

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaFileExport,
  FaEye,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/lubeRequirementsReport";

const toStr = (v, def = "") => (v === null || v === undefined ? def : String(v).trim());
const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};
const toList = (arr) => (Array.isArray(arr) ? arr : []);

const ID_COL_RE = /(^id$|_id$|typeid|categoryid|branchid)/i;
const IMPORTANT_COL_RE = /name|reg|code|vehicle|item|total|qty|branch|store|supplier|req|uom|unit|rate|amount/i;

const exportToCsv = (rows, columns, filename = "lube-requirement-report.csv") => {
  if (!rows?.length) return;
  const headers = columns?.length ? columns : Object.keys(rows[0] || {});
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => `"${String(r?.[h] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const createDefaultFilters = (branchType, branchId) => ({
  branchId: branchType === "HO" ? "Select" : String(branchId),
  monthName: "Select",
  year: "Select",
  itemCategoryId: "",
  itemId: "Select",
  supplierNames: [],
});

export default function LubeRequirementReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "BRANCH";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = Number(branchIdRaw || 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "BRANCH"),
      loginUserId: Number(loginUserId || 0),
      userId: toStr(userId || "0"),
    }),
    [sessionBranchId, branchType, loginUserId, userId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  const didInitRef = useRef(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const isBusy = pageLoading || dropdownLoading || listLoading;
  const [showFilters, setShowFilters] = useState(true);
  const [branches, setBranches] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const [itemCategory, setItemCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fieldClass = `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const displayColumns = useMemo(() => {
    const all = columns?.length ? columns : rows?.[0] ? Object.keys(rows[0]) : [];
    if (!all.length) return [];
    const kept = all.filter((c) => IMPORTANT_COL_RE.test(String(c)) && !ID_COL_RE.test(String(c)));
    return kept.length ? kept : all;
  }, [columns, rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [rows, currentPage]);

  const loadItems = useCallback(
    async (itemCategoryId) => {
      try {
        if (!itemCategoryId || isSelect(itemCategoryId)) {
          setItems([]);
          return;
        }
        const { data } = await axios.get(`${API_BASE}/items`, {
          params: { itemCategoryId },
          headers: commonHeaders,
        });
        setItems(toList(data?.rows));
      } catch (err) {
        console.error(err);
        setItems([]);
        toast.error(err?.response?.data?.message || err?.message || "Failed to load items");
      }
    },
    [commonHeaders]
  );

  const loadSuppliers = useCallback(
    async (branchIdValue) => {
      try {
        if (!branchIdValue || isSelect(branchIdValue)) {
          setSuppliers([]);
          return;
        }
        const { data } = await axios.get(`${API_BASE}/suppliers`, {
          params: { branchId: branchIdValue },
          headers: commonHeaders,
        });
        setSuppliers(toList(data?.rows));
      } catch (err) {
        console.error(err);
        setSuppliers([]);
        toast.error(err?.response?.data?.message || err?.message || "Failed to load suppliers");
      }
    },
    [commonHeaders]
  );

  const loadMeta = useCallback(async () => {
    try {
      setDropdownLoading(true);
      const [brRes, moRes, yrRes, catRes] = await Promise.all([
        axios.get(`${API_BASE}/branches`, {
          params: { branchType: session.branchType, branchId: session.branchId },
          headers: commonHeaders,
        }),
        axios.get(`${API_BASE}/months`, { headers: commonHeaders }),
        axios.get(`${API_BASE}/years`, { headers: commonHeaders }),
        axios.get(`${API_BASE}/itemCategory`, { headers: commonHeaders }),
      ]);
      setBranches(toList(brRes?.data?.rows));
      setMonths(toList(moRes?.data?.rows));
      setYears(toList(yrRes?.data?.rows));
      const cat = catRes?.data?.row || null;
      setItemCategory(cat);
      const nextItemCategoryId = String(cat?.itemCategoryId || "");
      setFilters((prev) => ({
        ...prev,
        branchId: session.branchType === "HO" ? prev.branchId : String(session.branchId),
        itemCategoryId: nextItemCategoryId,
      }));
      if (nextItemCategoryId) await loadItems(nextItemCategoryId);
      if (session.branchType !== "HO") await loadSuppliers(String(session.branchId));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load meta");
      setBranches([]);
      setMonths([]);
      setYears([]);
      setItemCategory(null);
      setItems([]);
      setSuppliers([]);
    } finally {
      setDropdownLoading(false);
    }
  }, [session.branchType, session.branchId, commonHeaders, loadItems, loadSuppliers]);

  const buildListParams = useCallback(
    (f = filters, override = {}) => ({
      branchId: session.branchType === "HO" ? (isSelect(f.branchId) ? "Select" : toStr(f.branchId)) : String(session.branchId),
      monthName: isSelect(f.monthName) ? "Select" : toStr(f.monthName),
      year: isSelect(f.year) ? "Select" : toStr(f.year),
      itemCategoryId: toStr(f.itemCategoryId),
      itemId: isSelect(f.itemId) ? "" : toStr(f.itemId),
      suppliers: Array.isArray(f.supplierNames) ? f.supplierNames : [],
      date: "",
      ...override,
    }),
    [filters, session.branchType, session.branchId]
  );

  const loadList = useCallback(
    async (nextFilters = filters, silent = false) => {
      try {
        setListLoading(true);
        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(nextFilters),
          headers: commonHeaders,
        });
        if (!data?.success) throw new Error(data?.message || "Search failed");
        const list = toList(data?.rows);
        setRows(list);
        setColumns(list[0] ? Object.keys(list[0]) : []);
        setCurrentPage(1);
        if (!silent) toast.success(`Found ${list.length} record(s)`);
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || err?.message || "Search failed");
        setRows([]);
        setColumns([]);
        setCurrentPage(1);
      } finally {
        setListLoading(false);
      }
    },
    [filters, buildListParams, commonHeaders]
  );

  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      setFilters(createDefaultFilters(session.branchType, session.branchId));
      setRows([]);
      setColumns([]);
      setSuppliers([]);
      setCurrentPage(1);
      await loadMeta();
    } finally {
      setPageLoading(false);
    }
  }, [session.branchType, session.branchId, loadMeta]);

  const handleChange = useCallback(
    async (e) => {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
      if (name === "branchId" && session.branchType === "HO") {
        setFilters((prev) => ({ ...prev, branchId: value, supplierNames: [] }));
        await loadSuppliers(value);
      }
      if (name === "itemCategoryId") {
        setFilters((prev) => ({ ...prev, itemCategoryId: value, itemId: "Select" }));
        await loadItems(value);
      }
    },
    [session.branchType, loadSuppliers, loadItems]
  );

  const toggleSupplier = useCallback((supplierName) => {
    setFilters((prev) => {
      const exists = prev.supplierNames.includes(supplierName);
      return {
        ...prev,
        supplierNames: exists
          ? prev.supplierNames.filter((x) => x !== supplierName)
          : [...prev.supplierNames, supplierName],
      };
    });
  }, []);

  const validate = useCallback((f = filters) => {
    if (isSelect(f.branchId)) return "Please select Branch";
    if (isSelect(f.monthName)) return "Please select Month";
    if (isSelect(f.year)) return "Please select Year";
    if (isSelect(f.itemCategoryId)) return "Item Category missing";
    return "";
  }, [filters]);

  const handleSearch = useCallback(async () => {
    const msg = validate(filters);
    if (msg) return toast.warning(msg);
    await loadList(filters);
  }, [filters, validate, loadList]);

  const handleViewAll = useCallback(async () => {
    const nextFilters = { ...filters, itemId: "Select", supplierNames: [] };
    const msg = validate(nextFilters);
    if (msg) return toast.warning(msg);
    setFilters(nextFilters);
    await loadList(nextFilters, true);
    toast.info("Showing all records for selected filters");
  }, [filters, validate, loadList]);

  const handleClearFilters = useCallback(async () => {
    const cleared = {
      ...createDefaultFilters(session.branchType, session.branchId),
      itemCategoryId: toStr(itemCategory?.itemCategoryId || ""),
    };
    setFilters(cleared);
    setRows([]);
    setColumns([]);
    setCurrentPage(1);
    if (session.branchType === "HO") setSuppliers([]);
    else await loadSuppliers(String(session.branchId));
    toast.info("Cleared filters");
  }, [session.branchType, session.branchId, itemCategory, loadSuppliers]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    toast.info("Page refreshed");
  }, [loadPage]);

  const handleExport = useCallback(() => {
    if (!rows.length) return toast.warning("No data to export");
    exportToCsv(rows, displayColumns, "lube-requirement-report.csv");
    toast.success("Exported CSV");
  }, [rows, displayColumns]);

  const handlePrint = useCallback(() => {
    const msg = validate(filters);
    if (msg) return toast.warning(msg);
    const suppliersCsv =
      Array.isArray(filters.supplierNames) && filters.supplierNames.length
        ? filters.supplierNames.join(",") + ","
        : "";
    navigate("/vehicle/LubeRequirementsReport_Print", {
      state: {
        filters: {
          branchId:
            session.branchType === "HO"
              ? isSelect(filters.branchId) ? "" : toStr(filters.branchId)
              : String(session.branchId),
          monthName: toStr(filters.monthName),
          year: toStr(filters.year),
          itemCategoryId: toStr(filters.itemCategoryId),
          itemId: isSelect(filters.itemId) ? "" : toStr(filters.itemId),
          suppliers: suppliersCsv,
          date: "",
        },
      },
    });
  }, [filters, validate, navigate, session.branchType, session.branchId]);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage();
  }, [loadPage]);

  const statusText = pageLoading
    ? "Loading page..."
    : dropdownLoading
    ? "Loading dropdowns..."
    : listLoading
    ? "Searching..."
    : "Ready";

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">LUBE REQUIREMENT REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white" : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>Lube Requirement Report</h2>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={handlePrint} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaPrint /> Print</button>
              <button onClick={handleExport} disabled={isBusy || !rows.length} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy || !rows.length ? "opacity-60 cursor-not-allowed" : ""}`}><FaFileExport /> Export</button>
              <button onClick={handleRefresh} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSyncAlt /> Refresh</button>
              <button onClick={() => navigate(-1)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaArrowLeft /> Back</button>
              <button onClick={() => setShowFilters((s) => !s)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleClearFilters} type="button" disabled={isBusy} className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>Clear</button>
                  <button onClick={handleViewAll} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} disabled={isBusy} type="button" className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSearch /> {listLoading ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select name="branchId" value={filters.branchId} onChange={handleChange} className={fieldClass} disabled={isBusy || session.branchType !== "HO"}>
                    <option value="Select">Select</option>
                    {branches.map((b, idx) => (
                      <option key={idx} value={String(b.branchId ?? b.fld_branchid ?? b.id ?? "")}>
                        {b.branchName ?? b.fld_BranchName ?? b.name ?? ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Month</label>
                  <select name="monthName" value={filters.monthName} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    {months.map((m) => <option key={m.value} value={m.name}>{m.name}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    {years.map((y) => <option key={y.year} value={y.year}>{y.year}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Item Category</label>
                  <input className={fieldClass} value={itemCategory?.itemCategoryName || "FUELS AND LUBRICANTS"} readOnly />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Item Name</label>
                  <select name="itemId" value={filters.itemId} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    {items.map((it) => (
                      <option key={it.itemId ?? it.fld_itemid} value={String(it.itemId ?? it.fld_itemid)}>
                        {it.itemName ?? it.fld_itemname}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0 flex items-end">
                  <div className={`text-sm pb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Suppliers selected: <b>{filters.supplierNames.length}</b>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label className={labelClass}>Suppliers</label>
                <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 p-3 rounded-lg border max-h-40 overflow-auto ${isDark ? "border-gray-700 bg-gray-900/20" : "border-gray-200 bg-white"}`}>
                  {suppliers.length === 0 ? (
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>No suppliers found for selected branch</div>
                  ) : (
                    suppliers.map((s) => {
                      const supplierName = s.supplierName ?? s.fld_suppliername ?? "";
                      const supplierId = s.supplierId ?? s.fld_supplierid ?? supplierName;
                      return (
                        <label key={supplierId} className={`flex items-center gap-2 text-sm min-w-0 cursor-pointer ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                          <input type="checkbox" checked={filters.supplierNames.includes(supplierName)} onChange={() => toggleSupplier(supplierName)} disabled={isBusy} />
                          <span className="truncate">{supplierName}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
              Total Records: {rows.length}
            </div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{statusText}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || dropdownLoading || listLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Select Branch, Month and Year, then click Search.</p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    {displayColumns.map((c) => (
                      <th key={c} className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {pagedRows.map((r, idx) => (
                    <tr key={idx} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                      {displayColumns.map((c) => (
                        <td key={c} className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{String(r?.[c] ?? "")}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, rows.length)} of {rows.length} entries
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}